import { ArrowRight, DiceFive, SlidersHorizontal } from '@phosphor-icons/react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { StageVideoBackdrop } from '@/app/agent/StageVideoBackdrop.tsx';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { str } from '@/app/lib/a2ui/props.ts';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';

/**
 * Client half of `TripKickoff` — screen zero, and the only screen that mounts
 * the showreel backdrop, so the footage runs behind the entire stage here and
 * nowhere else (see `StageVideoBackdrop`).
 *
 * The screen itself stays a well-behaved natural-height fragment inside the
 * shell's content column: no viewport heights, no `fixed`, no full-bleed. It
 * does not need them — the backdrop already fills the window, so this screen is
 * just type and two choices laid over it, with generous vertical rhythm so the
 * footage breathes between them.
 *
 * Because the ground is now moving footage rather than a flat colour, text here
 * is white-on-scrim rather than themed body colour, and the do-it-yourself card
 * is glass: a themed dark card would disappear into the darker frames.
 *
 * The arrival screen carries NO demo notice, by the owner's decision: nothing is
 * at stake yet. The disclosure lives where consequence does — the travel
 * document screen and, later, the booking and payment steps — and those are
 * component-owned there so no model can drop or soften them.
 */

const messages = defineMessages({
  surprise: {
    id: 'tripKickoff.surprise',
    defaultMessage: 'Surprise me',
    description: 'Button: the agent decides the whole trip and pre-fills every choice',
  },
  surpriseFallback: {
    id: 'tripKickoff.surpriseFallback',
    defaultMessage: 'We pick everything — you just react.',
    description: 'Supporting line under the Surprise me button when the agent supplies none',
  },
  manual: {
    id: 'tripKickoff.manual',
    defaultMessage: 'I’ll do it myself',
    description: 'Button: the visitor fills in their own travel preferences',
  },
  manualFallback: {
    id: 'tripKickoff.manualFallback',
    defaultMessage: 'Set your own style, season, budget and dates.',
    description: 'Supporting line under the do-it-yourself button when the agent supplies none',
  },
  gameForFun: {
    id: 'tripKickoff.gameForFun',
    defaultMessage: 'Game for fun',
    description: 'Accent shortcut into the canyon balloon mini-game',
  },
});

const glassButtonStyle = {
  backgroundColor: 'hsl(var(--glass))',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
};

const glassBorderStyle = {
  background: 'hsl(var(--muted-foreground) / 0.18)',
  boxSizing: 'border-box' as const,
  padding: '1px',
  WebkitMask:
    'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  maskComposite: 'exclude',
};

const glassAccentStyle = {
  background:
    'linear-gradient(to right, hsl(var(--primary) / 0) 0%, hsl(var(--primary)) 50%, hsl(var(--primary) / 0) 100%)',
};

const glassAccentVerticalStyle = {
  background:
    'linear-gradient(to bottom, hsl(var(--primary) / 0) 0%, hsl(var(--primary)) 50%, hsl(var(--primary) / 0) 100%)',
};

export const TripKickoff: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const title = str(node.props.title);
  const subtitle = str(node.props.subtitle);
  const surpriseTeaser =
    str(node.props.surpriseTeaser) || intl.formatMessage(messages.surpriseFallback);
  const manualTeaser = str(node.props.manualTeaser) || intl.formatMessage(messages.manualFallback);

  return (
    <div
      className="stage-kickoff flex min-h-0 w-full flex-1 flex-col justify-center gap-14 pt-6 sm:gap-24 sm:pt-16"
      data-wayfarer-step="1"
      data-wayfarer-step-available-through="2"
    >
      <StageVideoBackdrop />

      <div className="relative z-10 animate-fadeUp motion-reduce:animate-none">
        <h1 className="w-full max-w-none text-balance text-[3.375rem] font-bold leading-[1.02] tracking-tighter text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.55)] sm:text-[5.625rem]">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-5 w-full max-w-none text-base leading-relaxed text-white/85 drop-shadow-[0_1px_12px_rgba(0,0,0,0.5)] sm:text-lg">
            {subtitle}
          </p>
        ) : null}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => dispatch?.('chooseMode', { mode: 'surprise' })}
          className="group relative z-10 flex min-h-[162px] items-stretch rounded-[15px] border-0 p-0 text-left text-white transition duration-200 [touch-action:manipulation] hover:-translate-y-[2px] active:translate-y-0 active:scale-[0.995] motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
          style={glassButtonStyle}
        >
          <span
            className="pointer-events-none absolute inset-0 z-0 rounded-[15px]"
            style={glassBorderStyle}
            aria-hidden="true"
          />
          <span
            className="pointer-events-none absolute inset-x-4 top-0 z-0 h-px rounded-full"
            style={glassAccentStyle}
            aria-hidden="true"
          />
          <div className="relative z-10 flex min-w-0 flex-1 items-center gap-4 px-5 py-5 sm:gap-5 sm:px-7 sm:py-7">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-border bg-background/30 text-primary">
              <DiceFive
                className="h-8 w-8"
                size={32}
                color="currentColor"
                weight="regular"
                aria-hidden="true"
              />
            </span>
            <span className="min-w-0">
              <span className="block text-2xl font-bold text-white sm:text-3xl">
                {intl.formatMessage(messages.surprise)}
              </span>
              <span className="mt-2 block text-sm leading-normal text-white/70 sm:text-base">
                {surpriseTeaser}
              </span>
            </span>
          </div>
          <span className="relative z-10 flex w-20 shrink-0 items-center justify-center sm:w-24">
            <span
              className="pointer-events-none absolute inset-y-0 left-0 w-px"
              style={glassAccentVerticalStyle}
              aria-hidden="true"
            />
            <ArrowRight
              className="h-8 w-8 text-primary transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none"
              size={32}
              color="currentColor"
              weight="regular"
              aria-hidden="true"
            />
          </span>
        </button>

        <button
          type="button"
          onClick={() => dispatch?.('chooseMode', { mode: 'manual' })}
          className="group relative z-10 flex min-h-[162px] items-stretch rounded-[15px] border-0 p-0 text-left text-white transition duration-200 [touch-action:manipulation] hover:-translate-y-[2px] active:translate-y-0 active:scale-[0.995] motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
          style={glassButtonStyle}
        >
          <span
            className="pointer-events-none absolute inset-0 z-0 rounded-[15px]"
            style={glassBorderStyle}
            aria-hidden="true"
          />
          <span
            className="pointer-events-none absolute inset-x-4 top-0 z-0 h-px rounded-full"
            style={glassAccentStyle}
            aria-hidden="true"
          />
          <div className="relative z-10 flex min-w-0 flex-1 items-center gap-4 px-5 py-5 sm:gap-5 sm:px-7 sm:py-7">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-border bg-background/30 text-primary">
              <SlidersHorizontal
                className="h-8 w-8"
                size={32}
                color="currentColor"
                weight="regular"
                aria-hidden="true"
              />
            </span>
            <span className="min-w-0">
              <span className="block text-2xl font-bold text-white sm:text-3xl">
                {intl.formatMessage(messages.manual)}
              </span>
              <span className="mt-2 block text-sm leading-normal text-white/70 sm:text-base">
                {manualTeaser}
              </span>
            </span>
          </div>
          <span className="relative z-10 flex w-20 shrink-0 items-center justify-center sm:w-24">
            <span
              className="pointer-events-none absolute inset-y-0 left-0 w-px"
              style={glassAccentVerticalStyle}
              aria-hidden="true"
            />
            <ArrowRight
              className="h-8 w-8 text-primary transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none"
              size={32}
              color="currentColor"
              weight="regular"
              aria-hidden="true"
            />
          </span>
        </button>
        <div className="relative z-10 flex translate-y-5 justify-center sm:col-span-2">
          <button
            type="button"
            onClick={() => dispatch?.('chooseMode', { mode: 'game' })}
            className="rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-lg transition duration-150 [touch-action:manipulation] hover:brightness-110 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
          >
            {intl.formatMessage(messages.gameForFun)}
          </button>
        </div>
      </div>
    </div>
  );
};
